// src/DashboardLayout.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  VStack,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useDisclosure,
  useBreakpointValue,
  Image,
  Collapse,
  Text,
  Link as ChakraLink,
  HStack,
  Avatar,
  Icon,
  Button
} from '@chakra-ui/react';
import {
  HamburgerIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@chakra-ui/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './firebaseConfig';
import useOrgSettings from './hooks/useOrgSettings';

// Global verification banner
import EmailVerificationNotice from './components/EmailVerificationNotice';

// Sales Reports collapsible group
import SalesReportsGroup from './components/sidebar/SalesReportsGroup';

// Centralised menu config
import { MENU } from './menu.config';

// ===== Constants =====
const SIDEBAR_W = 220;
const COLLAPSED_W = 80;
const TOP_BAR_H = '56px';
const STORAGE_KEY = 'fh.sidebar.collapsed';

// Global listener cleanup guard
if (!window.fixihubUnsubs) window.fixihubUnsubs = [];
function cleanupAllListeners() {
  try {
    window.fixihubUnsubs.forEach((u) => {
      try { u && u(); } catch {}
    });
  } catch {}
  window.fixihubUnsubs = [];
}

// ===== Sidebar Menu (generic) =====
function SidebarMenu({ items, onLinkClick }) {
  const location = useLocation();
  const [open, setOpen] = useState({ Settings: true });
  const toggle = (label) => setOpen((p) => ({ ...p, [label]: !p[label] }));

  const isActive = (path) =>
    location.pathname === path ||
    (location.pathname.startsWith(path + '/') && path !== '/leads');

  return (
    <VStack align="start" spacing={1} px={1} w="full">
      {items.map((item) => (
        <Box key={item.label} w="full">
          {item.children ? (
            <>
              <Box
                as="button"
                w="full"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                onClick={() => toggle(item.label)}
                py={2}
                _hover={{ bg: 'whiteAlpha.200' }}
                rounded="md"
              >
                <Text fontWeight="bold" color="white" noOfLines={1}>
                  {item.label}
                </Text>
                {open[item.label] ? (
                  <ChevronDownIcon color="white" />
                ) : (
                  <ChevronRightIcon color="white" />
                )}
              </Box>

              <Collapse in={open[item.label]} animateOpacity style={{ width: '100%' }}>
                <VStack align="start" spacing={1} w="full">
                  {item.children.map((sub) => {
                    const active = isActive(sub.href);
                    return (
                      <ChakraLink
                        as={Link}
                        to={sub.href}
                        key={sub.label}
                        onClick={onLinkClick}
                        fontSize="sm"
                        py={2}
                        pl={4}
                        pr={3}
                        w="full"
                        rounded="md"
                        color={active ? 'teal.200' : 'gray.200'}
                        fontWeight={active ? 'semibold' : 'normal'}
                        bg={active ? 'whiteAlpha.200' : 'transparent'}
                        _hover={{
                          color: 'cyan.300',
                          bg: 'whiteAlpha.100',
                          textDecoration: 'none'
                        }}
                        _focus={{ outline: 'none', boxShadow: 'none' }}
                      >
                        <HStack spacing={2} align="center">
                          {sub.icon ? <Icon as={sub.icon} boxSize={4} /> : null}
                          <Text as="span">{sub.label}</Text>
                        </HStack>
                      </ChakraLink>
                    );
                  })}
                </VStack>
              </Collapse>
            </>
          ) : (
            <ChakraLink
              as={Link}
              to={item.href}
              display="block"
              py={2}
              rounded="md"
              fontWeight="medium"
              onClick={onLinkClick}
              color={isActive(item.href) ? 'teal.300' : 'white'}
              bg={isActive(item.href) ? 'whiteAlpha.200' : 'transparent'}
              _hover={{ color: 'cyan.300', bg: 'whiteAlpha.100', textDecoration: 'none' }}
              _focus={{ outline: 'none', boxShadow: 'none' }}
              w="full"
            >
              {item.label}
            </ChakraLink>
          )}
        </Box>
      ))}
    </VStack>
  );
}

// Company branding
const CompanyBranding = ({ logoUrl, companyName }) => {
  const hasLogo = Boolean(logoUrl);
  return (
    <HStack spacing={{ base: 2, sm: 3 }} align="center">
      {hasLogo ? (
        <Image
          src={logoUrl}
          alt={`${companyName} logo`}
          boxSize="32px"
          objectFit="contain"
          rounded="md"
        />
      ) : (
        <Avatar name={companyName} bg="whiteAlpha.400" size="sm" />
      )}
      <Text fontWeight="semibold" display={{ base: 'none', sm: 'block' }}>
        {companyName}
      </Text>
    </HStack>
  );
};

// ===== Layout =====
export default function DashboardLayout({ children }) {
  const navigate = useNavigate();
  const { settings } = useOrgSettings();

  const isMobile = useBreakpointValue({ base: true, md: false });
  const drawer = useDisclosure();

  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => () => cleanupAllListeners(), []);

  const brandBg = settings?.primaryColor || '#1A202C';
  const logoUrl = (settings?.logoUrl || '').trim();
  const company = (settings?.companyName || 'Fixihub').trim();

  const handleLogout = async () => {
    try {
      cleanupAllListeners();
      await signOut(auth);
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const handleHamburger = () => {
    if (isMobile) {
      drawer.onOpen();
    } else {
      setCollapsed((c) => !c);
    }
  };

  const sidebarWidth = useMemo(
    () => (isMobile ? 0 : collapsed ? COLLAPSED_W : SIDEBAR_W),
    [collapsed, isMobile]
  );
  const mainContentMl = useMemo(() => (isMobile ? 0 : sidebarWidth), [sidebarWidth, isMobile]);

  return (
    <Flex direction="column" minH="100vh">
      {/* Top Bar */}
      <Flex
        bg={brandBg}
        color="white"
        px={4}
        py={3}
        justify="start"
        align="center"
        shadow="sm"
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={100}
        h={TOP_BAR_H}
      >
        <HStack spacing={3} alignItems="center">
          <IconButton
            icon={<HamburgerIcon />}
            onClick={handleHamburger}
            variant="ghost"
            colorScheme="whiteAlpha"
            aria-label="Toggle menu"
            display="flex"
          />
          <CompanyBranding logoUrl={logoUrl} companyName={company} />
        </HStack>
      </Flex>

      {/* Main container */}
      <Flex flex="1" mt={TOP_BAR_H}>
        {/* Desktop Sidebar */}
        <Box
          display={{ base: 'none', md: 'block' }}
          bg={brandBg}
          color="white"
          p={4}
          flexShrink={0}
          w={`${sidebarWidth}px`}
          position="fixed"
          left={0}
          top={TOP_BAR_H}
          h={`calc(100vh - ${TOP_BAR_H})`}
          overflowY="auto"
          overflowX="hidden"
          transition="width 180ms ease"
          zIndex={99}
        >
          <Collapse in={!collapsed} animateOpacity>
            <VStack align="start" w="full" spacing={1}>
              {/* Base items */}
              <SidebarMenu items={MENU.base} onLinkClick={() => {}} />

              {/* Sales Reports Group */}
              <SalesReportsGroup />

              {/* Settings group */}
              <SidebarMenu items={[MENU.settings]} onLinkClick={() => {}} />

              {/* ✅ Logout above footer */}
              <Box w="full" pt={4}>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  size="sm"
                  w="full"
                  color="white"
                  borderColor="whiteAlpha.400"
                  _hover={{ bg: 'whiteAlpha.200' }}
                >
                  Logout
                </Button>
              </Box>

              {/* Footer info */}
              <VStack align="center" px={4} mt={8} w="full">
                <Image src="/images/logo.png" alt="FixiHub logo" maxW="100px" objectFit="contain" mb={2} />
                <Text fontSize="xs" color="whiteAlpha.700" textAlign="center" noOfLines={1}>
                  Suggestions/Bugs/Help?
                </Text>
                <ChakraLink
                  href="mailto:developers@fixihub.pro"
                  fontSize="xs"
                  color="teal.300"
                  fontWeight="semibold"
                  textDecoration="underline"
                >
                  Contact us.
                </ChakraLink>
              </VStack>
            </VStack>
          </Collapse>
        </Box>

        {/* Mobile Drawer */}
        <Drawer isOpen={drawer.isOpen} placement="left" onClose={drawer.onClose}>
          <DrawerOverlay />
          <DrawerContent bg={brandBg} color="white" maxW="280px">
            <DrawerCloseButton />
            <DrawerHeader>
              <CompanyBranding logoUrl={logoUrl} companyName={company} />
            </DrawerHeader>
            <DrawerBody overflowY="auto" pt={0}>
              <VStack align="start" w="full" spacing={1}>
                <SidebarMenu items={MENU.base} onLinkClick={drawer.onClose} />
                <SalesReportsGroup />
                <SidebarMenu items={[MENU.settings]} onLinkClick={drawer.onClose} />

                {/* ✅ Logout above footer */}
                <Box w="full" pt={4}>
                  <Button
                    onClick={async () => {
                      drawer.onClose();
                      await handleLogout();
                    }}
                    variant="outline"
                    size="sm"
                    w="full"
                    color="white"
                    borderColor="whiteAlpha.400"
                    _hover={{ bg: 'whiteAlpha.200' }}
                  >
                    Logout
                  </Button>
                </Box>

                {/* Footer */}
                <VStack align="center" px={4} mt={8} w="full">
                  <Image src="/images/logo.png" alt="FixiHub logo" maxW="100px" objectFit="contain" mb={2} />
                  <Text fontSize="xs" color="whiteAlpha.700" textAlign="center" noOfLines={1}>
                    Suggestions/Bugs/Help?
                  </Text>
                  <ChakraLink
                    href="mailto:developers@fixihub.pro"
                    fontSize="xs"
                    color="teal.300"
                    fontWeight="semibold"
                    textDecoration="underline"
                  >
                    Contact us.
                  </ChakraLink>
                </VStack>
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        {/* Main Content */}
        <Box
          as="main"
          flex="1"
          minW={0}
          ml={{ base: 0, md: mainContentMl }}
          transition="margin-left 180ms ease"
          p={{ base: 3, md: 6 }}
          bg="gray.50"
        >
          <Flex direction="column" w="full">
            <Box mb={3}>
              <EmailVerificationNotice />
            </Box>
            {children}
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
}
